import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuLabel, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { 
  Settings, 
  Download, 
  Building2, 
  User, 
  LogOut, 
  Users, 
  ChevronDown,
  Globe,
  Presentation
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useClientAuth } from '@/hooks/useClientAuth';
import type { Client } from '@/types/client';

interface DashboardHeaderProps {
  client: Client;
}

export function DashboardHeader({ client }: DashboardHeaderProps) {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { clients, switchClient } = useClientAuth();
  const [presentationMode, setPresentationMode] = useState(false);

  const handleSignOut = async () => {
    await signOut();
    navigate('/espace-client');
  };

  const handleExport = () => {
    // TODO: Implement export functionality
    console.log('Export dashboard data');
  };

  const togglePresentationMode = () => {
    setPresentationMode(!presentationMode);
    if (!presentationMode) {
      document.documentElement.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  return (
    <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50">
      <div className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Client Info */}
          <div className="flex items-center space-x-4">
            {client.brand_logo_url ? (
              <img
                src={client.brand_logo_url}
                alt={client.name}
                className="w-10 h-10 rounded-lg object-cover"
              />
            ) : (
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Building2 className="w-5 h-5 text-primary" />
              </div>
            )}
            
            <div>
              <h1 className="text-xl font-bold">{client.name}</h1>
              <div className="flex items-center space-x-2">
                {client.marketplaces.map((marketplace) => (
                  <Badge key={marketplace} variant="secondary" className="text-xs">
                    <Globe className="w-3 h-3 mr-1" />
                    {marketplace.replace('amazon.', '').toUpperCase()}
                  </Badge>
                ))}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center space-x-3">
            {/* Presentation Mode */}
            <Button
              variant="outline"
              size="sm"
              onClick={togglePresentationMode}
            >
              <Presentation className="w-4 h-4 mr-2" />
              {presentationMode ? 'Quitter' : 'Présentation'}
            </Button>

            {/* Export */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleExport}
            >
              <Download className="w-4 h-4 mr-2" />
              Exporter
            </Button>

            {/* Settings */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/dashboard/${client.slug}/parametres`)}
            >
              <Settings className="w-4 h-4 mr-2" />
              Paramètres
            </Button>

            {/* Client Switcher */}
            {clients.length > 1 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Building2 className="w-4 h-4 mr-2" />
                    Changer de client
                    <ChevronDown className="w-4 h-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>Clients disponibles</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {clients.map((c) => (
                    <DropdownMenuItem
                      key={c.id}
                      onClick={() => {
                        switchClient(c.slug);
                        navigate(`/dashboard/${c.slug}`);
                      }}
                      className={c.id === client.id ? 'bg-muted' : ''}
                    >
                      <div className="flex items-center space-x-2">
                        <div className="w-6 h-6 bg-primary/10 rounded flex items-center justify-center">
                          <Building2 className="w-3 h-3" />
                        </div>
                        <span>{c.name}</span>
                      </div>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user?.user_metadata?.avatar_url} />
                    <AvatarFallback>
                      {user?.email?.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end">
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">
                      {user?.user_metadata?.full_name || user?.email}
                    </p>
                    <p className="text-xs leading-none text-muted-foreground">
                      {user?.email}
                    </p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/dashboard')}>
                  <Users className="mr-2 h-4 w-4" />
                  Changer de client
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/profile')}>
                  <User className="mr-2 h-4 w-4" />
                  Profil
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleSignOut}>
                  <LogOut className="mr-2 h-4 w-4" />
                  Se déconnecter
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </header>
  );
}